import React from 'react';
import { Check } from 'lucide-react';
export const ComparisonSection = () => {
  return <section className="py-20 bg-[#050505] relative overflow-hidden" id="comparison" data-id="element-2180">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-50" data-id="element-2181" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-50" data-id="element-2182" />

      <div className="max-w-6xl mx-auto px-4 relative z-10" data-id="element-2183">
        <div className="text-center mb-16" data-id="element-2184">
          <div className="inline-block bg-[#FF0000]/10 border border-[#FF0000]/30 px-4 py-1.5 rounded-full mb-6" data-id="element-2185">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-[#FF0000]" data-id="element-2186">
              PURPOSE-BUILT
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6 uppercase tracking-tight leading-tight" data-id="element-2187">
            Built for Relationships.
            <br data-id="element-2188" />
            Not for Everything.
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-mono leading-relaxed" data-id="element-2189">
            General-purpose AI is trained to be polite and helpful.
            ToxicDetector is trained to detect the patterns inside real
            relationship conversations — the way a therapist or forensic analyst
            reads them.
          </p>
        </div>

        {/* Comparison Table */}
        <div className="overflow-x-auto pb-4" data-id="element-2190">
          <div className="min-w-[700px]" data-id="element-2191">
            {/* Table Header */}
            <div className="grid grid-cols-3 gap-6 mb-6" data-id="element-2192">
              <div className="p-4 flex items-end" data-id="element-2193">
                <span className="text-gray-500 font-mono text-sm uppercase tracking-wider" data-id="element-2194">
                  Feature Comparison
                </span>
              </div>
              <div className="p-6 bg-gray-900/50 rounded-t-xl border-t border-x border-gray-800 text-center" data-id="element-2195">
                <h3 className="text-gray-400 font-bold text-xl uppercase tracking-wide" data-id="element-2196">
                  Generic AI Tools
                </h3>
                <p className="text-gray-600 text-xs font-mono mt-2" data-id="element-2197">
                  (general-purpose)
                </p>
              </div>
              <div className="relative p-6 bg-[#FF0000]/10 rounded-t-xl border-t border-x border-[#FF0000] text-center shadow-[0_-10px_30px_rgba(255,0,0,0.15)]" data-id="element-2198">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#FF0000] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-md" data-id="element-2199">
                  Forensic Grade
                </div>
                <h3 className="text-[#FF0000] font-black text-2xl uppercase tracking-wider filter drop-shadow-[0_0_8px_rgba(255,0,0,0.5)]" data-id="element-2200">
                  ToxicDetector
                </h3>
                <p className="text-[#FF0000]/80 text-xs font-mono mt-2" data-id="element-2201">
                  (Specialized AI)
                </p>
              </div>
            </div>

            {/* Table Rows */}
            <div className="space-y-3" data-id="element-2202">
              <ComparisonRow feature="Domain Focus" competitor="Everything (general)" us="Relationship communication only" highlight data-id="element-2203" />
              <ComparisonRow feature="Tone Reading" competitor="Surface-level" us="Slang, sarcasm, subtext, microaggressions" highlight data-id="element-2204" />
              <ComparisonRow feature="Output Style" competitor="Neutral, vague" us="Specific patterns, named tactics, severity score" highlight isQuote data-id="element-2205" />
              <ComparisonRow feature="Privacy Model" competitor="May train on inputs" us="Anonymous · encrypted · never trained on" highlight data-id="element-2206" />
              <ComparisonRow feature="Use Case" competitor="Q&A, writing, code" us="Emotional clarity for hard conversations" highlight data-id="element-2207" />
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center" data-id="element-2208">
          <div className="inline-block p-1 bg-gradient-to-r from-[#FF0000] via-[#FFD700] to-[#FF0000] rounded-xl shadow-2xl" data-id="element-2209">
            <div className="bg-black rounded-lg px-10 py-8 max-w-2xl" data-id="element-2210">
              <h3 className="text-2xl font-bold text-white mb-3 uppercase tracking-wide" data-id="element-2211">
                Ready for emotional clarity?
              </h3>
              <p className="text-gray-400 mb-8 font-mono text-sm" data-id="element-2212">
                Stop guessing the dynamic. Start naming it.
              </p>
              <button onClick={() => window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })} className="bg-[#FF0000] text-white px-10 py-4 font-black uppercase tracking-wider hover:bg-red-600 transition-all shadow-[0_0_20px_rgba(255,0,0,0.4)] hover:shadow-[0_0_30px_rgba(255,0,0,0.6)] rounded-lg transform hover:-translate-y-1" data-id="element-2213">
                Start Free Scan
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
const ComparisonRow = ({
  feature,
  competitor,
  us,
  highlight = false,
  isQuote = false
}: {
  feature: string;
  competitor: string;
  us: string;
  highlight?: boolean;
  isQuote?: boolean;
}) => {
  return <div className="grid grid-cols-3 gap-6 group" data-id="element-2214">
      {/* Feature Name */}
      <div className="p-5 flex items-center border-b border-gray-800" data-id="element-2215">
        <span className="text-gray-300 font-bold uppercase text-sm tracking-wide" data-id="element-2216">
          {feature}
        </span>
      </div>

      {/* Competitor Value */}
      <div className="p-5 bg-gray-900/30 border-x border-gray-800 flex items-center justify-center text-center rounded-lg" data-id="element-2217">
        <span className={`text-gray-500 font-mono text-sm ${isQuote ? 'italic' : ''}`} data-id="element-2218">
          {competitor}
        </span>
      </div>

      {/* Our Value */}
      <div className="p-5 bg-[#FF0000]/5 border border-[#FF0000]/30 flex items-center justify-center text-center relative overflow-hidden group-hover:bg-[#FF0000]/10 transition-colors rounded-lg shadow-[0_0_10px_rgba(255,0,0,0.05)]" data-id="element-2219">
        {highlight && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#FF0000]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-2220" />}
        <div className="flex items-center gap-3 relative z-10" data-id="element-2221">
          <span className={`text-white font-bold text-sm md:text-base ${isQuote ? 'font-black text-[#FF0000] italic' : ''}`} data-id="element-2222">
            {us}
          </span>
          <Check size={18} className="text-[#FFD700]" strokeWidth={3} data-id="element-2223" />
        </div>
      </div>
    </div>;
};